import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Eye, Megaphone, User, Shield, Send } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const ForumTopic = () => {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [topic, setTopic] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [adminResponse, setAdminResponse] = useState('');
  const [responses, setResponses] = useState([]);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadTopic();
    loadResponses();
  }, [topicId]);

  const loadTopic = async () => {
    if (topicId.startsWith('ann-')) {
      try {
        const response = await axios.get(`${API_URL}/api/announcements`);
        const annId = topicId.replace('ann-', '');
        const announcement = response.data.find(a => a.id === annId);
        if (announcement) {
          setTopic({
            id: topicId,
            title: announcement.title || 'Admin Announcement',
            content: announcement.message,
            author: announcement.author_name || 'Admin',
            timestamp: new Date(announcement.created_at).toLocaleDateString(),
            replies: 0,
            views: 0,
            isAnnouncement: true
          });
        }
      } catch (error) {
        console.error('Failed to load announcement:', error);
      }
    } else {
      try {
        const response = await axios.get(`${API_URL}/api/forum/topics/${topicId}`);
        const t = response.data;
        setTopic({
          id: t.id,
          title: t.title,
          content: t.content,
          category: t.category,
          author: t.author,
          timestamp: new Date(t.created_at).toLocaleDateString(),
          replies: t.replies || 0,
          views: t.views || 0,
          isAnnouncement: false
        });
      } catch (error) {
        console.error('Failed to load topic:', error);
      }
    }
  };

  const loadResponses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/forum/topics/${topicId}/responses`);
      setResponses(response.data);
    } catch (error) {
      console.error('Failed to load responses:', error);
    }
  };

  const handleReply = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    toast.info('Reply functionality will be implemented in future updates');
    setReplyContent('');
  };

  const handleAdminResponse = async () => {
    if (!adminResponse.trim()) {
      toast.error('Please enter a response');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/forum/topics/${topicId}/responses`, 
        { content: adminResponse },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Admin response posted successfully');
      setAdminResponse('');
      loadResponses();
    } catch (error) {
      console.error('Failed to post response:', error);
      toast.error('Failed to post response');
    }
  };

  const formatContent = (content) => {
    if (!content) return '';
    
    return content
      .split('\n')
      .map((line, index) => {
        line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        
        if (/^\d+\./.test(line.trim())) {
          return `<li key="${index}" class="ml-4">${line.replace(/^\d+\.\s*/, '')}</li>`;
        }
        
        if (line.trim().startsWith('-')) {
          return `<li key="${index}" class="ml-4">${line.replace(/^-\s*/, '')}</li>`;
        }
        
        if (line.trim() === '') {
          return '<br />';
        }
        
        return `<p key="${index}" class="mb-2">${line}</p>`;
      })
      .join('');
  };

  if (!topic) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600 mb-4">Loading topic...</p>
          <Link to="/forum" className="text-[#8B1818] hover:underline">
            Back to Forum
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Header */}
      <div className="py-12 bg-[#F9F9F7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/forum" className="inline-flex items-center text-[#8B1818] hover:text-[#701313] mb-4 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Forum
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
            {topic.title}
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Original Post */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-6">
          {/* Post Header */}
          <div className={`px-6 py-4 border-b border-slate-200 ${topic.isAnnouncement ? 'bg-[#8B1818]' : 'bg-slate-50'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`h-10 w-10 rounded-full flex items-center justify-center ${topic.isAnnouncement ? 'bg-white/20' : 'bg-[#8B1818]'}`}>
                  {topic.isAnnouncement ? (
                    <Megaphone className="h-5 w-5 text-white" />
                  ) : (
                    <User className="h-5 w-5 text-white" />
                  )}
                </div>
                <div>
                  <p className={`font-semibold ${topic.isAnnouncement ? 'text-white' : 'text-slate-900'}`}>
                    {topic.author}
                  </p>
                  <p className={`text-sm ${topic.isAnnouncement ? 'text-white/70' : 'text-slate-500'}`}>
                    Posted {topic.timestamp}
                  </p>
                </div>
              </div>
              {!topic.isAnnouncement && (
                <div className="flex items-center space-x-4 text-sm text-slate-500">
                  <span className="flex items-center">
                    <Eye className="h-4 w-4 mr-1" />
                    {topic.views} views
                  </span>
                  <span className="flex items-center">
                    <MessageSquare className="h-4 w-4 mr-1" />
                    {topic.replies} replies
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Post Content */}
          <div className="px-6 py-6">
            <div 
              className="prose prose-slate max-w-none text-slate-700"
              dangerouslySetInnerHTML={{ __html: formatContent(topic.content) }}
            />
          </div>
        </div>

        {/* Admin Responses Section */}
        {responses.length > 0 && (
          <div className="mb-6 space-y-4">
            <h3 className="text-lg font-bold text-slate-900">Admin Responses</h3>
            {responses.map((response) => (
              <div key={response.id} className="bg-white rounded-xl shadow-sm border-2 border-[#8B1818]/20 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 bg-[#8B1818]/5">
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 rounded-full bg-[#8B1818] flex items-center justify-center">
                      <Shield className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm flex items-center">
                        {response.admin_name}
                        <span className="ml-2 px-2 py-0.5 bg-[#8B1818] text-white text-xs rounded">Admin</span>
                      </p>
                      <p className="text-xs text-slate-500">{new Date(response.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
                <div className="px-6 py-4">
                  <p className="text-slate-700">{response.content}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Admin Response Form - Only visible to admins */}
        {user?.is_admin && !topic.isAnnouncement && (
          <div className="bg-white rounded-xl shadow-sm border-2 border-[#8B1818] p-6 mb-6">
            <div className="flex items-center mb-4">
              <Shield className="h-5 w-5 text-[#8B1818] mr-2" />
              <h3 className="text-lg font-bold text-slate-900">Admin Response</h3>
            </div>
            <p className="text-sm text-slate-600 mb-4">As an admin, you can post an official response to this topic.</p>
            <textarea
              value={adminResponse}
              onChange={(e) => setAdminResponse(e.target.value)}
              placeholder="Write your official admin response..."
              rows={4}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#8B1818] focus:border-transparent resize-none mb-4"
            />
            <button
              onClick={handleAdminResponse}
              className="bg-[#8B1818] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#701313] transition-colors flex items-center"
            >
              <Send className="h-4 w-4 mr-2" />
              Post Admin Response
            </button>
          </div>
        )}

        {/* Regular Reply Section */}
        {!topic.isAnnouncement && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Leave a Reply</h3>
            {user ? (
              <div>
                <textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="Write your reply..."
                  rows={4}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#8B1818] focus:border-transparent resize-none"
                />
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={handleReply}
                    className="bg-[#8B1818] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#701313] transition-colors"
                  >
                    Post Reply
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-slate-600 mb-4">You must be logged in to reply</p>
                <Link
                  to="/login"
                  className="inline-block bg-[#8B1818] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#701313] transition-colors"
                >
                  Login to Reply
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Sample Replies */}
        {!topic.isAnnouncement && topic.replies > 0 && (
          <div className="mt-6 space-y-4">
            <h3 className="text-lg font-bold text-slate-900">Community Replies ({topic.replies})</h3>
            
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                    <User className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">CommunityMember</p>
                    <p className="text-xs text-slate-500">1 day ago</p>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4">
                <p className="text-slate-700">Thank you for this detailed explanation! Very helpful for understanding the topic better.</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center">
                    <User className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">LegalPractitioner</p>
                    <p className="text-xs text-slate-500">3 days ago</p>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4">
                <p className="text-slate-700">Great insights! I would also add that proper documentation is key to avoiding future disputes.</p>
              </div>
            </div>

            <div className="text-center pt-4">
              <button className="text-[#8B1818] hover:underline font-medium">
                Load more replies...
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForumTopic;
